// src/pages/PhoneVerificationPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Header from '../components/layout/Header';
import ApiService from '../services/ApiService';
import { useAuth } from '../contexts/AuthContext';
import '../styles/phone-verification.css';
import SEOHead from '../components/SEO/SEOHead';

const PhoneVerificationPage = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  
  // États
  const [phoneNumber, setPhoneNumber] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [code, setCode] = useState(['', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSendingWa, setIsSendingWa] = useState(false); // repli WhatsApp (ajout)
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [canResend, setCanResend] = useState(true);
  
  // Références pour les inputs
  const inputRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null)
  ];
  
  // Timer interval
  const timerInterval = useRef(null);
  
  // Fonction pour masquer le numéro de téléphone
  const maskPhoneNumber = (phone) => {
    if (!phone) return '';
    // S'assurer qu'il y a assez de caractères
    if (phone.length >= 6) {
        return phone.substring(0, 1) + '****' + phone.substring(5);
    }
    // Si le numéro est trop court, on masque différemment ou retourne tel quel
    // Par exemple, retourner les 2 derniers chiffres masqués
    if (phone.length > 2) {
        return phone.substring(0, phone.length - 2) + '**';
    }
    return '**';
  };
  
   // Initialisation du numéro de téléphone
  useEffect(() => {
    let phone = '';

    // 1. Essayer depuis deliveryInfo dans localStorage
    const deliveryInfo = localStorage.getItem('deliveryInfo');
    if (deliveryInfo) {
      try {
        const deliveryData = JSON.parse(deliveryInfo);
        phone = deliveryData?.phone || '';
      } catch (error) {
        // console.error('Erreur lors de la lecture de deliveryInfo:', error);
        // Optionnel: naviguer vers delivery si données corrompues?
        // navigate('/delivery');
        // return; // Sortir du useEffect si les données sont critiques
      }
    }

    // 2. Si pas trouvé dans localStorage, vérifier si passé via state de navigation
    // (Cela dépend de comment vous naviguez vers cette page)
    // if (!phone && location.state?.deliveryPhone) {
    //   phone = location.state.deliveryPhone;
    // }

    // 3. Si toujours pas de numéro, redirection
    if (!phone) {
        // console.warn('Aucun numéro de téléphone trouvé dans deliveryInfo. Redirection vers /delivery.');
        navigate('/delivery');
        return; // Important: sortir si on redirige
     }

    // 4. Si un numéro est trouvé, le définir dans l'état
    setPhoneNumber(phone);
    setMaskedPhone(maskPhoneNumber(phone));

    // Le focus sur inputRefs[0] est mieux géré après l'envoi du code,
    // car les inputs pour le code ne sont pas visibles au départ.
    // Donc, on retire cette partie d'ici.

    // Nettoyage du timer au démontage
    return () => {
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
      }
    };
  }, [navigate]); // Dépendances minimales: navigate est stable
  
  // Gestion du timer
  useEffect(() => {
    if (timeRemaining > 0) {
      timerInterval.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
      }
    }
    
    return () => {
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
      }
    };
  }, [timeRemaining]);
  
  // Formater le temps restant
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  
  // Envoyer le code SMS
  const handleSendCode = async () => {
    if (!canResend || isSending) return;
    
    setIsSending(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${ApiService.getBaseUrl()}/sms/?action=send_verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          telephone: phoneNumber,
          orderId: orderId
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Affiche le canal réellement utilisé (repli WhatsApp automatique)
        setSuccess(
          data.canal === 'whatsapp'
            ? 'Le SMS n\'a pas pu être envoyé : code envoyé via WhatsApp !'
            : 'Code envoyé avec succès !'
        );
        setCanResend(false);
        setTimeRemaining(180); // 3 minutes

        // Focus sur le premier champ
        if (inputRefs[0].current) {
          inputRefs[0].current.focus();
        }
      } else {
        setError(data.message || 'Erreur lors de l\'envoi du code');
      }
    } catch (error) {
      // console.error('Erreur envoi SMS:', error);
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setIsSending(false);
    }
  };

  // Recevoir le code via WhatsApp (repli manuel — ajout)
  // Renvoie le code COURANT (déjà généré) sur WhatsApp. Utile quand le SMS
  // « part » mais n'arrive jamais. Aucune régénération de code.
  const handleSendWhatsApp = async () => {
    if (isSendingWa) return;

    setIsSendingWa(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${ApiService.getBaseUrl()}/whatsapp/?action=resend_whatsapp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          telephone: phoneNumber,
          orderId: orderId
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Code envoyé via WhatsApp !');
        setCanResend(false);
        if (!timeRemaining) {
          setTimeRemaining(180); // affiche la saisie du code si pas déjà fait
        }
        if (inputRefs[0].current) {
          inputRefs[0].current.focus();
        }
      } else {
        setError(data.message || 'Impossible d\'envoyer le code via WhatsApp');
      }
    } catch (error) {
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setIsSendingWa(false);
    }
  };
  
  // Gérer le changement dans les inputs
  const handleCodeChange = (index, value) => {
    // Accepter uniquement les chiffres
    if (value && !/^\d$/.test(value)) return;
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    
    // Si un chiffre est entré, passer au suivant
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
    
    // Si les 4 chiffres sont entrés, vérifier automatiquement
    if (index === 3 && value) {
      const fullCode = newCode.join('');
      if (fullCode.length === 4) {
        verifyCode(fullCode);
      }
    }
  };
  
  // Gérer le retour arrière
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };
  
  // Gérer le collage
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 4);
    
    if (/^\d+$/.test(pastedData)) {
      const newCode = pastedData.split('').concat(['', '', '', '']).slice(0, 4);
      setCode(newCode);
      
      // Focus sur le dernier champ rempli ou le suivant
      const lastFilledIndex = newCode.findIndex(digit => digit === '');
      if (lastFilledIndex === -1) {
        inputRefs[3].current?.focus();
        verifyCode(newCode.join(''));
      } else {
        inputRefs[lastFilledIndex].current?.focus();
      }
    }
  };
  
  // Vérifier le code
  const verifyCode = async (fullCode) => {
    if (isLoading) return;
    
    setIsLoading(true);
    setError('');
    
    // Désactiver tous les inputs pendant la vérification
    inputRefs.forEach(ref => {
      if (ref.current) ref.current.disabled = true;
    });
    
    try {
      const response = await fetch(`${ApiService.getBaseUrl()}/sms/?action=verify_code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          telephone: phoneNumber,
          code: fullCode,
          orderId: orderId
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Numéro vérifié avec succès !');
        
        // Mettre à jour le statut local de l'utilisateur
        if (user) {
          user.telephone_verifie = 1;
        }
        
        // Rediriger vers la page de commande après un court délai
        setTimeout(() => {
          navigate(`/order/${orderId}`, { replace: true });
        }, 1000);
      } else {
        setError(data.message || 'Code incorrect');
        setCode(['', '', '', '']);
        
        // Réactiver les inputs et focus sur le premier
        inputRefs.forEach(ref => {
          if (ref.current) ref.current.disabled = false;
        });
        inputRefs[0].current?.focus();
      }
    } catch (error) {
      // console.error('Erreur vérification:', error);
      setError('Erreur de connexion. Veuillez réessayer.');
      
      // Réactiver les inputs
      inputRefs.forEach(ref => {
        if (ref.current) ref.current.disabled = false;
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="phone-verification-page">
    <SEOHead
      title="Vérification du téléphone"
      noindex
    />
      <Header 
        title="Vérification du numéro" 
        showBackButton={true} 
        onBackClick={() => navigate('/delivery')}
      />
      
      <div className="verification-content">
        <div className="verification-card">
          <div className="verification-icon">
            <svg width="60" height="60" viewBox="0 0 24 24" fill="#dc1010">
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
            </svg>
          </div>
          
          <h2 className="verification-title">Vérifiez votre numéro</h2>
          
          <p className="verification-subtitle">
            Un code de vérification sera envoyé au numéro
          </p>
          
          <div className="phone-display">
            {maskedPhone}
          </div>
          
          {!timeRemaining && (
            <button
              onClick={handleSendCode}
              disabled={!canResend || isSending}
              className="send-code-button"
            >
              {isSending ? (
                <>
                  <div className="button-spinner"></div>
                  Envoi en cours...
                </>
              ) : (
                'Recevoir le code SMS'
              )}
            </button>
          )}
          
          {timeRemaining > 0 && (
            <>
              <div className="code-inputs">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={inputRefs[index]}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className="code-input"
                    disabled={isLoading}
                  />
                ))}
              </div>
              
              <div className="timer-info">
                <p className="timer-text">
                  Renvoyer le code dans {formatTime(timeRemaining)}
                </p>
                
                {timeRemaining === 0 && (
                  <button
                    onClick={handleSendCode}
                    className="resend-link"
                    disabled={isSending}
                  >
                    Renvoyer le code
                  </button>
                )}
              </div>
            </>
          )}
          
          {error && (
            <div className="error-message">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              {error}
            </div>
          )}
          
          {success && (
            <div className="success-message">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
              </svg>
              {success}
            </div>
          )}
          
          <div className="verification-help">
            <p>Vous n'avez pas reçu de code ?</p>
            <p className="help-subtitle">
              Vérifiez que le numéro {maskedPhone} est correct et actif.
            </p>

            {/* Repli WhatsApp (ajout) : renvoie le code courant sur WhatsApp */}
            <button
              type="button"
              onClick={handleSendWhatsApp}
              disabled={isSendingWa}
              className="whatsapp-otp-link"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21 5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2zm0 18.15c-1.52 0-3.01-.41-4.31-1.18l-.31-.18-3.12.82.83-3.04-.2-.31a8.21 8.21 0 0 1-1.26-4.37c0-4.54 3.7-8.23 8.24-8.23 4.54 0 8.23 3.69 8.23 8.23s-3.69 8.23-8.23 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.07s.89 2.4 1.01 2.56c.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.28z"/>
              </svg>
              {isSendingWa ? 'Envoi en cours…' : 'Recevoir le code sur WhatsApp'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneVerificationPage;